import { OFFICIALS_PAGE_SIZE_DEFAULT } from "@seagames/contracts";
import { z } from "zod";
import { cleanText, idParam, pageParam, sizeParam, textParam } from "../nocs/list-params";

/**
 * Officials: the filters, the page and the open official live in the URL
 * (`/officials?q&noc&sport&page&size&official`) so Back, a reload and a
 * copied link show the same page. A bad value is dropped, never an error page.
 */
export const officialsSearch = z.object({
	/** A name, any part, or a card number. */
	q: textParam,
	noc: idParam,
	sport: idParam,
	page: pageParam,
	size: sizeParam,
	/** The official whose drawer is open (an `ac_card_id`). */
	official: idParam,
});
export type OfficialsSearch = z.infer<typeof officialsSearch>;

export interface OfficialsDraft extends Record<string, unknown> {
	q: string;
	noc: string;
	sport: string;
}

export const EMPTY_OFFICIALS_DRAFT: OfficialsDraft = { q: "", noc: "", sport: "" };

export const officialsDraftFrom = (s: OfficialsSearch): OfficialsDraft => ({ q: s.q ?? "", noc: s.noc ?? "", sport: s.sport ?? "" });

/** Draft → URL: empties dropped, the page starts over; the page size stays. */
export const officialsSearchFrom = (d: OfficialsDraft, keep: Pick<OfficialsSearch, "size"> = {}): OfficialsSearch => ({
	q: cleanText(d.q),
	noc: d.noc || undefined,
	sport: d.sport || undefined,
	size: keep.size,
});

export const officialsFilterCount = (s: OfficialsSearch): number => [s.q, s.noc, s.sport].filter((v) => v !== undefined).length;

export const officialsPaging = (s: OfficialsSearch) => ({ page: s.page ?? 1, pageSize: s.size ?? OFFICIALS_PAGE_SIZE_DEFAULT });

/** The `GET /officials` query — snake_case, as the wire wants it. */
export interface OfficialsParams {
	games_id: string;
	search?: string;
	responsible_organization_id?: string;
	sport_id?: string;
	page: number;
	page_size: number;
}

export function officialsParams(gamesId: string, s: OfficialsSearch): OfficialsParams {
	const { page, pageSize } = officialsPaging(s);
	return { games_id: gamesId, search: s.q, responsible_organization_id: s.noc, sport_id: s.sport, page, page_size: pageSize };
}
